import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { adminAction } from "@/lib/adminApi";
import {
  AdminPageHeader,
  AdminPageSkeleton,
  AdminSection,
  AdminSegmented,
  AdminMeter,
  AdminPanel,
  AdminTable,
  AdminEmptyState,
  AdminStatRibbon,
  adminTableHeadCellClass,
  adminTableRowClass,
  adminTableCellClass,
} from "./AdminUI";
import { Activity } from "lucide-react";

const RANGE_OPTS = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "all", label: "All time" },
];

export function AdminUsageTab() {
  const [range, setRange] = useState<string>("all");

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "usage", range],
    queryFn: () => adminAction("usage", { range: range === "all" ? undefined : range }),
  });

  const { data: funnel, isLoading: funnelLoading } = useQuery({
    queryKey: ["admin", "onboarding_funnel"],
    queryFn: () => adminAction("onboarding_funnel"),
  });

  if (isLoading && funnelLoading) {
    return <AdminPageSkeleton stats={3} rows={5} columns={3} />;
  }

  const usage = data || [];
  const totalProposals = usage.reduce((s: number, u: any) => s + (u.proposals_created || 0), 0);
  const activeOrgs = usage.filter((u: any) => u.last_active).length;

  return (
    <div className="space-y-10">
      <AdminPageHeader
        eyebrow="Analytics"
        title="Usage"
        description="Onboarding momentum and per-organisation activity across the platform."
      />

      {/* Onboarding Funnel */}
      <AdminSection
        title="Onboarding funnel"
        description="From sign-up to first teammate invite — where customers drop off."
      >
        {funnel ? (
          <AdminPanel padded>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-5 max-w-4xl">
              <AdminMeter
                label="Signed up"
                value={funnel.signed_up}
                total={funnel.signed_up || 1}
              />
              <AdminMeter
                label="Uploaded tender"
                value={funnel.uploaded_rfp}
                total={funnel.signed_up || 1}
              />
              <AdminMeter
                label="Created proposal"
                value={funnel.created_proposal}
                total={funnel.signed_up || 1}
              />
              <AdminMeter
                label="Invited teammate"
                value={funnel.invited_teammate}
                total={funnel.signed_up || 1}
              />
            </div>
          </AdminPanel>
        ) : null}
      </AdminSection>

      {/* Usage Table */}
      <AdminSection
        title="Usage by organisation"
        description="Active workspaces ranked by proposal output in the selected window."
        action={<AdminSegmented value={range} onChange={setRange} options={RANGE_OPTS} size="sm" />}
      >
        <AdminStatRibbon
          items={[
            { label: "Organisations", value: usage.length, sub: "In window" },
            { label: "Active", value: activeOrgs, sub: "Logged in recently" },
            { label: "Proposals", value: totalProposals, sub: "Total created" },
          ]}
        />

        {usage.length === 0 ? (
          <AdminEmptyState
            icon={Activity}
            title="No usage in this window"
            description="Try widening the range to see organisation-level activity."
          />
        ) : (
          <AdminTable
            head={
              <>
                <th className={adminTableHeadCellClass}>Organisation</th>
                <th className={adminTableHeadCellClass}>Proposals created</th>
                <th className={adminTableHeadCellClass}>Last active</th>
              </>
            }
          >
            {usage.map((u: any, i: number) => (
              <tr
                key={u.id}
                className={adminTableRowClass}
                style={{ borderBottom: i === usage.length - 1 ? "none" : "1px solid rgba(58,53,48,0.06)" }}
              >
                <td className={`${adminTableCellClass} text-foreground`}>{u.name}</td>
                <td className={`${adminTableCellClass} tabular-nums text-foreground`}>{u.proposals_created}</td>
                <td className={`${adminTableCellClass} text-muted-foreground tabular-nums`}>
                  {u.last_active
                    ? new Date(u.last_active).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                    : "—"}
                </td>
              </tr>
            ))}
          </AdminTable>
        )}
      </AdminSection>
    </div>
  );
}
